// src/data/decisionsClient.js
//
// P-15: DECISIONS-REGISTER-API
// Client for /api/decisions — generic decisions register.
//
// Endpoints covered:
//   getDecisions(params)         GET  /api/decisions          (viewer+)
//   createDecision(payload)      POST /api/decisions          (editor+)
//   updateDecision(id, patch)    PATCH /api/decisions/:id     (editor+)
//   deleteDecision(id)           DELETE /api/decisions/:id    (editor+)
//   restoreDecision(id)          POST /api/decisions/:id/restore (editor+)
//   getDeletedDecisions()        GET  /api/decisions/deleted   (editor+)

import { NetworkError, errorFromResponse } from "./errors.js";
import { log } from "../utils/logger.js";

const BASE = "/api/decisions";

// ─── Internal fetch helper ────────────────────────────────────────────────────

async function apiFetch(url, options = {}) {
  let res;
  try {
    res = await fetch(url, {
      ...options,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
    });
  } catch (err) {
    log.error("decisionsClient: network failure", { url, err: String(err) });
    throw new NetworkError(`Failed to reach ${url}`, err);
  }
  if (!res.ok) {
    throw await errorFromResponse(res);
  }
  if (res.status === 204) return null;
  return res.json();
}

// ─── Reads ────────────────────────────────────────────────────────────────────

/**
 * Fetch active decisions with optional filters.
 * @param {{ search?: string, status?: string }} [params]
 * @returns {Promise<Array>}
 */
export async function getDecisions(params = {}) {
  log.ui("decisionsClient: getDecisions", params);
  const qs = new URLSearchParams();
  if (params.search) qs.set("search", params.search);
  if (params.status) qs.set("status", params.status);
  const url = qs.toString() ? `${BASE}?${qs}` : BASE;
  return apiFetch(url);
}

/**
 * Fetch soft-deleted decisions (for "Recently deleted" section). Editor+.
 * @returns {Promise<Array>}
 */
export async function getDeletedDecisions() {
  log.ui("decisionsClient: getDeletedDecisions");
  return apiFetch(`${BASE}/deleted`);
}

// ─── Writes ───────────────────────────────────────────────────────────────────

/**
 * Create a new decision. Editor+.
 * @param {{ entityId: string, decision: string, entityType?: string, expectedAction?: string, ownerName?: string, targetDate?: string, status?: string }} payload
 * @returns {Promise<object>}  Created decision row (camelCase)
 */
export async function createDecision(payload) {
  log.ui("decisionsClient: createDecision", { entityId: payload?.entityId });
  return apiFetch(BASE, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/**
 * Update mutable decision fields. Editor+.
 * @param {string} id  Decision UUID
 * @param {{ decision?: string, expectedAction?: string, ownerName?: string, targetDate?: string, status?: string }} patch
 * @returns {Promise<object>}  Updated decision row (camelCase)
 */
export async function updateDecision(id, patch) {
  log.ui("decisionsClient: updateDecision", { id });
  return apiFetch(`${BASE}/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

/**
 * Soft-delete a decision. Editor+.
 * @param {string} id  Decision UUID
 * @returns {Promise<null>}
 */
export async function deleteDecision(id) {
  log.ui("decisionsClient: deleteDecision", { id });
  return apiFetch(`${BASE}/${encodeURIComponent(id)}`, { method: "DELETE" });
}

/**
 * Restore a soft-deleted decision. Editor+.
 * @param {string} id  Decision UUID
 * @returns {Promise<object>}  Restored decision row (camelCase)
 */
export async function restoreDecision(id) {
  log.ui("decisionsClient: restoreDecision", { id });
  return apiFetch(`${BASE}/${encodeURIComponent(id)}/restore`, { method: "POST" });
}
